/**
 * /earthworks/tickets — the field-ticket register (EW-5, GET /earthworks/tickets).
 *
 * Filters are the API's: status (chips), job (a picker). The register is
 * paginated by the API, so the band's count is the response's `total`, not a
 * tally of the loaded rows; no per-status count is drawn because no endpoint
 * returns one. The subtotal is what the customer signed, or on a draft the
 * priced lines so far (the API's draftSubtotal, labelled as such); absent → `—`.
 *
 * Office roles only (the API's EW_RATE_READ): a field_worker reads their
 * tickets on the job's phone screen, without prices.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { useEwJobs } from '../../hooks/useEarthworks';
import { useEwTickets } from '../../hooks/useEwTickets';
import { ewDay, fmtMoney } from '../../lib/earthworks';
import { EW_TICKET_STATUSES, canWorkEwTickets, ewTicketSkin } from '../../lib/ew-tickets';
import type { EwTicket } from '../../types/earthworks';
import { useOpenRecord } from '../downhole/recordPage';
import { EW_EYEBROW, EW_INPUT, EwBadge, EwChip, EwChipRow, EwEmpty, EwPanel, EwRegister, type EwColumn } from './EwBits';

/** What the money column means on this row. */
export function ewTicketMoney(t: Pick<EwTicket, 'status' | 'subtotal' | 'draftSubtotal' | 'currency'>): { value: string; note: string | null } {
  if (t.status === 'draft') return { value: fmtMoney(t.draftSubtotal, t.currency), note: 'so far' };
  return { value: fmtMoney(t.subtotal, t.currency), note: null };
}

export default function EwTicketsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const canRead = canWorkEwTickets(user?.role);
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const jobId = params.get('job') ?? '';
  const [q, setQ] = useState('');
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };

  const query = useEwTickets({ status: status || undefined, job_id: jobId || undefined, q: q.trim() || undefined }, canRead);
  const jobs = useEwJobs();
  const rows = query.data?.data ?? [];
  const total = query.data?.total ?? null;

  const columns: EwColumn<EwTicket>[] = [
    { key: 'number', header: 'Ticket', render: (t) => <span className="font-mono font-bold text-white">{t.ticketNumber}</span> },
    { key: 'job', header: 'Job', render: (t) => (<><span className="font-mono">{t.jobNumber || '—'}</span><p className="text-xs text-fieldiq-muted">{t.siteName || '—'}</p></>) },
    { key: 'customer', header: 'Customer', render: (t) => t.customerName || '—' },
    { key: 'day', header: 'Work day', render: (t) => (t.mode === 'per_day' ? ewDay(t.workDate) : t.modeLabel || 'Whole job') },
    { key: 'status', header: 'Status', render: (t) => <EwBadge skin={ewTicketSkin(t.status)} value={t.status} /> },
    { key: 'lines', header: 'Lines', align: 'right', render: (t) => (<>{t.lineCount}{t.unpricedCount > 0 && <p className="text-xs font-bold text-fi-amber">{t.unpricedCount} unpriced</p>}</>) },
    {
      key: 'subtotal',
      header: 'Subtotal',
      align: 'right',
      render: (t) => {
        const m = ewTicketMoney(t);
        return (<><span className="font-mono">{m.value}</span>{m.note && m.value !== '—' && <p className="text-xs text-fieldiq-muted">{m.note}</p>}</>);
      },
    },
  ];

  return (
    <div data-testid="ew-tickets-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={EW_EYEBROW}
        title="Field Tickets"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <FileText className="size-4" aria-hidden="true" />, label: 'Tickets', tone: 'neutral', loading: query.isLoading, value: total === null ? '—' : String(total) },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canRead ? (
            <EwPanel eyebrow="Field tickets" title="Tickets">
              <EwEmpty>Field tickets carry prices, so the register is for the office, supervisors and dispatch. Your tickets are on each job's screen.</EwEmpty>
            </EwPanel>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <EwChipRow label="Status">
                  <EwChip label="All" active={!status} onClick={() => set('status', '')} />
                  {EW_TICKET_STATUSES.map((s) => (
                    <EwChip key={s.value} label={s.label} active={status === s.value} onClick={() => set('status', s.value)} />
                  ))}
                </EwChipRow>
                <select className={`${EW_INPUT} w-auto`} aria-label="Job" value={jobId} onChange={(e) => set('job', e.target.value)}>
                  <option value="">All jobs</option>
                  {(jobs.data?.data ?? []).map((j) => (
                    <option key={j.id} value={j.id}>{j.jobNumber}{j.siteName ? ` · ${j.siteName}` : ''}</option>
                  ))}
                </select>
                <input className={`${EW_INPUT} w-64`} aria-label="Search tickets" placeholder="Ticket, job or customer" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <EwPanel
                eyebrow="Field tickets"
                title="Tickets"
                testId="ew-tickets-register"
                footer={!query.isLoading && total !== null ? `Showing ${rows.length} of ${total}` : undefined}
              >
                <EwRegister
                  query={query}
                  rows={rows}
                  columns={columns}
                  rail={(t) => ewTicketSkin(t.status).rail}
                  onOpen={(t) => openRecord(`/earthworks/tickets/${t.id}`)}
                  subject="field tickets"
                  testId="ew-ticket-row"
                  minWidth="min-w-[960px]"
                  empty={<EwEmpty>No field tickets{status || jobId ? ' match these filters' : ' yet'}. A ticket is built from a job's daily logs, on the job's Tickets tab.</EwEmpty>}
                  card={(t) => (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-mono text-sm font-bold text-white">{t.ticketNumber}</p>
                          <p className="mt-0.5 truncate text-xs text-fieldiq-muted">{t.jobNumber} · {t.customerName || '—'}</p>
                        </div>
                        <EwBadge skin={ewTicketSkin(t.status)} value={t.status} />
                      </div>
                      <p className="mt-2 font-mono text-sm text-white">{ewTicketMoney(t).value}</p>
                    </>
                  )}
                />
              </EwPanel>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
